import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import './index.scss';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Corrige os ícones padrão do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Monkey patch para ignorar erro no _clearLines
const originalClearLines = L.Routing.Control.prototype._clearLines;
L.Routing.Control.prototype._clearLines = function () {
  try {
    originalClearLines.call(this);
  } catch (e) {
    console.warn("Erro ignorado em _clearLines:", e.message);
  }
};

const MapComponent = ({ origin, destination, showUserLocation = false, showRoute = false }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const routingControlRef = useRef(null);
  const [routeSummary, setRouteSummary] = useState(null);
  const [userLocation, setUserLocation] = useState(origin);

  const localizarUsuario = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log("Localização precisa obtida:", latitude, longitude);
          setUserLocation({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.error("Erro ao obter localização:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      console.error("Geolocalização não é suportada.");
    }
  };

  useEffect(() => {
    if (showUserLocation) {
      localizarUsuario();
    }
  }, [showUserLocation]);

  useLayoutEffect(() => {
    if (!destination) return;

    if (routingControlRef.current) {
      routingControlRef.current.remove();
      routingControlRef.current = null;
    }

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    if (!mapRef.current) return;

    const map = L.map(mapRef.current, {
      center: showUserLocation && userLocation ? userLocation : destination,
      zoom: 13,
      zoomControl: true,
    });

    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    if (showUserLocation && userLocation) {
      L.marker(userLocation).addTo(map).bindPopup('Sua localização');
    }

    L.marker(destination).addTo(map).bindPopup('Destino');

    if (showRoute && showUserLocation && userLocation) {
      const control = L.Routing.control({
        waypoints: [L.latLng(userLocation), L.latLng(destination)],
        routeWhileDragging: false,
        addWaypoints: false,
        draggableWaypoints: false,
        showAlternatives: false,
        createMarker: () => null,
      }).addTo(map);

      routingControlRef.current = control;

      control.on('routesfound', function (e) {
        const route = e.routes[0];
        const distanceKm = (route.summary.totalDistance / 1000).toFixed(2);
        const durationMin = Math.round(route.summary.totalTime / 60);
        setRouteSummary({ distanceKm, durationMin });
      });

      control.on('routingerror', function () {
        setRouteSummary(null);
      });
    }

    const timeoutId = setTimeout(() => {
      if (mapInstanceRef.current && mapRef.current && mapRef.current.offsetParent !== null) {
        try {
          mapInstanceRef.current.invalidateSize();
        } catch (e) {
          console.warn("Erro ao chamar invalidateSize:", e.message);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      if (routingControlRef.current) {
        routingControlRef.current.remove();
        routingControlRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [userLocation, destination, showUserLocation, showRoute]);

  const googleMapsLink =
    destination
      ? showUserLocation && userLocation
        ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${destination.lat},${destination.lng}`
        : `https://www.google.com/maps/search/?api=1&query=${destination.lat},${destination.lng}`
      : '#';

  return (
    <div className="map-container">
      {routeSummary && (
        <div className="map-summary">
          {routeSummary.distanceKm} km, {routeSummary.durationMin} min
        </div>
      )}

      <div className="map-frame">
        <div ref={mapRef} id="leaflet-map" />
      </div>

      <div className="map-link">
        <a href={googleMapsLink} target="_blank" rel="noopener noreferrer">
          Ver no Google Maps
        </a>
        <button onClick={localizarUsuario} className="map-refresh-btn">
          Atualizar Localização
        </button>
      </div>
    </div>
  );
};

export default MapComponent;
